import React from "react";
import { Box, Container, Typography, Button, Grid, Card } from "@mui/material";
import { motion } from "framer-motion";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { Link } from "react-router-dom";

const supportFeatures = [
    {
        icon: <AccessTimeIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "24/7 Availability",
        description: "Round-the-clock support coverage across all North American time zones. Our team is always ready to assist you, day or night.",
    },
    {
        icon: <CloudDoneIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "Remote Troubleshooting",
        description: "Secure remote access capabilities allow us to diagnose and resolve issues quickly without on-site visits, minimizing downtime.",
    },
    {
        icon: <SpeedIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "Rapid Response",
        description: "Average response time under 15 minutes for critical issues. We prioritize your business continuity and system uptime.",
    },
    {
        icon: <MonitorHeartIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "Proactive Monitoring",
        description: "Continuous network monitoring with automated alerts. We identify and resolve potential issues before they impact your operations.",
    },
    {
        icon: <SecurityIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "Secure Access",
        description: "Enterprise-grade security protocols for all remote support sessions. Your data and systems remain protected at all times.",
    },
    {
        icon: <SupportAgentIcon sx={{ fontSize: 60, color: "#facd00" }} />,
        title: "Expert Technicians",
        description: "Certified professionals with extensive experience in network infrastructure, systems administration, and IT troubleshooting.",
    },
];

const HeroSection = () => (
    <Box
        sx={{
            position: "relative",
            height: { xs: "400px", sm: "500px", md: "600px" },
            overflow: "hidden",
            background: "linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #facd00 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <Container>
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
            >
                <Typography
                    variant="h1"
                    fontWeight="bold"
                    sx={{
                        color: "#fff",
                        textAlign: "center",
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                        fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                    }}
                >
                    24/7 Remote Support
                </Typography>
                <Typography
                    variant="h4"
                    mt={2}
                    sx={{
                        color: "#facd00",
                        textAlign: "center",
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                        fontSize: { xs: "1.2rem", sm: "1.8rem", md: "2.2rem" },
                    }}
                >
                    Always Available. Always Reliable. Always Secure.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 4 }}>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/bookit"
                        sx={{
                            borderRadius: 50,
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            backgroundColor: "#facd00",
                            color: "#000",
                            fontWeight: "bold",
                            "&:hover": {
                                backgroundColor: "#e6b800",
                            },
                        }}
                    >
                        Get Support Now
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{
                            borderRadius: 50,
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            borderColor: "#facd00",
                            color: "#facd00",
                            "&:hover": {
                                borderColor: "#e6b800",
                                backgroundColor: "rgba(250, 205, 0, 0.1)",
                            },
                        }}
                    >
                        Learn More
                    </Button>
                </Box>
            </motion.div>
        </Container>
    </Box>
);

const FeatureCard = ({ feature, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
    >
        <Card
            sx={{
                boxShadow: 4,
                borderRadius: 3,
                p: 3,
                height: "100%",
                textAlign: "center",
                transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: 8,
                },
            }}
        >
            <Box sx={{ mb: 2 }}>{feature.icon}</Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                {feature.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {feature.description}
            </Typography>
        </Card>
    </motion.div>
);

const SupportPage = () => {
    return (
        <>
            <HeroSection />
            <Container sx={{ py: 8 }}>
                <Box sx={{ mb: 6, textAlign: "center" }}>
                    <Typography variant="h3" fontWeight="bold" gutterBottom>
                        Comprehensive Remote Support
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: "900px", mx: "auto" }}>
                        Our 24/7 remote support team provides nationwide coverage with rapid response times,
                        proactive monitoring, and expert troubleshooting to keep your IT infrastructure running smoothly.
                    </Typography>
                </Box>
                <Grid container spacing={4}>
                    {supportFeatures.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <FeatureCard feature={feature} index={index} />
                        </Grid>
                    ))}
                </Grid>

                {/* Contact Section */}
                <Box
                    sx={{
                        mt: 8,
                        p: 6,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h4" fontWeight="bold" sx={{ color: "#fff", mb: 2 }}>
                        Need Immediate Assistance?
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#facd00", mb: 4 }}>
                        Our support team is standing by 24/7 to help you
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            component={Link}
                            to="/bookit"
                            sx={{
                                borderRadius: 50,
                                px: 5,
                                py: 2,
                                fontSize: "1.2rem",
                                backgroundColor: "#facd00",
                                color: "#000",
                                fontWeight: "bold",
                                "&:hover": {
                                    backgroundColor: "#e6b800",
                                },
                            }}
                        >
                            Schedule Support Call
                        </Button>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default SupportPage;
